import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiMail, FiChevronDown } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { LuBookX } from 'react-icons/lu';
import { HiArrowsRightLeft } from 'react-icons/hi2';
import ATable from '../../organisms/ATable';
import ACheckbox from '../../atoms/ACheckbox';
import CallRegistryModalForm from '../communication/contact/CallRegistryModalForm';
import ContactModalForm from '../communication/contact/ContactModalForm';
import ContactStatusSelector from '../communication/contact/ContactStatusSelector';
import SelectTextField from '../../molecules/SelectTextField';
import DraweeConfirmationStatusBadge from './shared/DraweeConfirmationStatusBadge';
import CheckStatusBadge from './shared/CheckStatusBadge';
import ConfirmationModal from '../../templates/ConfirmationModal';
import { formatCurrency, formatDate, formatCpfCnpj } from '../../../utils/collateralFormatters';
import useDraweeConfirmationPageEdit from '../../../hooks/useDraweeConfirmationPageEdit';

const HISTORY_LABELS: Record<string, string> = {
    CALL: 'Ligação registrada', EMAIL: 'Envio de email', WHATSAPP: 'Envio de WhatsApp',
    VALIDATED: 'Validação realizada', CONFIRMED: 'Confirmado', CONFIRM: 'Validação realizada',
    NOT_CONFIRMED: 'Não confirmado', UNMARKED: 'Desmarcado',
    UNDO_VALIDATION: 'Validação desfeita', REVOKE: 'Validação desfeita',
    REJECT_RECEIVABLE: 'Recebível não confirmado',
};

interface Props {
    show: boolean;
    onClose: () => void;
    analysisId: string;
    draweeConfirmationId: string;
    draweeExternalId?: string;
    debtorName: string;
    debtorDocument: string;
    debtorStatus?: string;
    draweeStatus?: string;
    receivables: any[];
    operationCode: string;
    history?: any[];
    onRefresh?: () => void;
}

const DC_STATUS_LABELS: Record<string, string> = {
    NOT_STARTED: 'Não iniciado', IN_PROGRESS: 'Em validação',
    CONFIRMED: 'Validado', PARTIALLY_CONFIRMED: 'Parcialmente validado', REJECTED: 'Rejeitado',
};

const DraweeConfirmationPageEdit = ({ show, onClose, analysisId, draweeConfirmationId, draweeExternalId, debtorName, debtorDocument, debtorStatus, draweeStatus, receivables: initialReceivables, operationCode, history: initialHistory, onRefresh }: Props) => {
    const {
        contact, loading, processing,
        showCallForm, setShowCallForm,
        farolStatus, setFarolStatus,
        showActionMenu, setShowActionMenu,
        notConfirmId, setNotConfirmId,
        notConfirmObs, setNotConfirmObs,
        notConfirmReason, setNotConfirmReason, NOT_CONFIRM_REASONS, handleReasonChange,
        selectedReceivables, setSelectedReceivables,
        showInteractionForm, setShowInteractionForm,
        showContactForm, setShowContactForm,
        showUndoConfirm, setShowUndoConfirm,
        receivables, history, dvStatus,
        isValidated, showSelectColumn, totals, deals,
        handleCreateContact, handleContactCreated,
        handleAction, handleValidateSelected,
        handleUndoValidation, handleMarkNotConfirmed,
        handleUnmarkNotConfirmed, onCallSuccess, handleInteractionSuccess,
    } = useDraweeConfirmationPageEdit({
        show, draweeConfirmationId, draweeExternalId, debtorName, debtorDocument,
        debtorStatus, draweeStatus, receivables: initialReceivables,
        operationCode, history: initialHistory, onRefresh,
    });

    if (!show) return null;

    return (
        <div className="h-full flex flex-col relative">
            {processing && (
                <div className="absolute inset-0 bg-white/60 dark:bg-slate-900/60 z-50 flex items-center justify-center">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                        Processando...
                    </div>
                </div>
            )}
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                    {contact && (
                        <Link to={`/communication/contact/${contact.id}/page-view/tab/deals`} title="Ver contato"
                            className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800">
                            <FiPhone size={16} />
                        </Link>
                    )}
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate max-w-[180px]" title={debtorName}>{debtorName}</h3>
                            <DraweeConfirmationStatusBadge status={dvStatus} statusFormatted={DC_STATUS_LABELS[dvStatus] || dvStatus} />
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-500">{formatCpfCnpj(debtorDocument)}</span>
                            {contact && (
                                <ContactStatusSelector
                                    contactId={contact.id}
                                    status={farolStatus}
                                    onStatusChange={(newStatus) => { setFarolStatus(newStatus); onRefresh?.(); }}
                                />
                            )}
                            {!contact && !loading && (
                                <button onClick={handleCreateContact} className="text-[10px] text-blue-600 hover:underline">+ Criar contato</button>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Action dropdown */}
                    {processing && (
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                            Processando...
                        </div>
                    )}
                    {!isValidated && contact && (
                        <div className="relative">
                            <button onClick={() => setShowActionMenu(showActionMenu === 'register' ? null : 'register')} disabled={loading || processing}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded text-xs hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50">
                                Registrar <FiChevronDown size={11} />
                            </button>
                            {showActionMenu === 'register' && (
                                <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded shadow-lg z-50 min-w-[130px]">
                                    <button onClick={() => handleAction('CALL')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FiPhone size={11} /> Ligação</button>
                                    <button onClick={() => handleAction('EMAIL')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FiMail size={11} /> Email</button>
                                    <button onClick={() => handleAction('WHATSAPP')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FaWhatsapp size={11} /> WhatsApp</button>
                                </div>
                            )}
                        </div>
                    )}
                    {!isValidated && contact && receivables.some((r: any) => r.canValidate) && (
                        <div className="relative">
                            <button onClick={() => setShowActionMenu(showActionMenu === 'validate' ? null : 'validate')} disabled={loading || processing || (totals.pendingCount > 0 && selectedReceivables.length === 0)}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                {totals.pendingCount === 0 ? 'Encerrar validação' : <>Validar {selectedReceivables.length > 0 && <span>({selectedReceivables.length})</span>}</>} <FiChevronDown size={11} />
                            </button>
                            {showActionMenu === 'validate' && (
                                <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded shadow-lg z-50 min-w-[150px]">
                                    <button onClick={() => handleValidateSelected('CALL')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FiPhone size={11} /> Por Ligação</button>
                                    <button onClick={() => handleValidateSelected('EMAIL')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FiMail size={11} /> Por Email</button>
                                    <button onClick={() => handleValidateSelected('WHATSAPP')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FaWhatsapp size={11} /> Por WhatsApp</button>
                                </div>
                            )}
                        </div>
                    )}
                    {isValidated && (
                        <button onClick={() => setShowUndoConfirm(true)} disabled={processing}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-100 text-amber-700 rounded text-xs hover:bg-amber-200 disabled:opacity-50">
                            <HiArrowsRightLeft size={12} /> Desfazer validação
                        </button>
                    )}
                </div>
            </div>

            {/* Body - flex column: contact + summary + receivables (flex-1) + history (fixed) */}
            <div className="flex-1 flex flex-col overflow-hidden" onClick={() => { setShowActionMenu(null); }}>
                <div className="p-4 space-y-3 flex-shrink-0">
                    {/* Contact info */}
                    {loading && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                            Carregando contato...
                        </div>
                    )}
                    {contact && (
                        <div className="bg-gray-50 dark:bg-slate-900 rounded p-2.5 flex flex-wrap gap-3">
                            {contact.phones.map((p: any) => (
                                <div key={p.id} className="flex items-center gap-1 text-sm dark:text-white">
                                    {p.whatsapp ? <FaWhatsapp className="text-green-500" size={13} /> : <FiPhone className="text-gray-500" size={13} />}
                                    <span>{p.number}</span>
                                </div>
                            ))}
                            {contact.emails.map((e: any) => (
                                <div key={e.id} className="flex items-center gap-1 text-sm dark:text-white">
                                    <FiMail className="text-gray-500" size={13} /><span>{e.address}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Summary */}
                    <div className="grid grid-cols-2 gap-1.5 text-center">
                        <div className="bg-gray-50 dark:bg-slate-900 rounded p-2">
                            <div className="text-lg font-semibold dark:text-white">{totals.count}</div>
                            <div className="text-xs text-gray-500">Recebíveis</div>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded p-2">
                            <div className="text-base font-semibold dark:text-white">{formatCurrency(totals.amount)}</div>
                            <div className="text-xs text-gray-500">Total</div>
                        </div>
                    </div>
                </div>

                {/* Receivables table - fills remaining space */}
                <div className="flex-1 overflow-auto px-4">
                    <ATable headers={[
                        { id: 'num', title: 'Duplicata', canSort: false, width: '80px' },
                        { id: 'parcela', title: 'Parcela', canSort: false, width: '65px' },
                        { id: 'due', title: 'Vencimento', canSort: false, width: '85px' },
                        { id: 'amt', title: 'Valor', canSort: false, cssClass: 'text-right', width: '100px' },
                        { id: 'st', title: 'Status', canSort: false, cssClass: 'text-center', width: '100px' },
                        { id: 'act', title: '', canSort: false, width: '30px' },
                    ]}
                    canSelectAll={showSelectColumn}
                    onSelect={checked => {
                        const selectableIds = receivables.filter(r => r.canReject).map(r => r.id);
                        setSelectedReceivables(checked ? selectableIds : []);
                    }}>
                        {receivables.map((r: any, i: number) => (
                            <Fragment key={r.id}>
                                <tr className={`group ${i % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}>
                                    {showSelectColumn && (
                                        <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-center">
                                            {r.canReject && (
                                                <ACheckbox checked={selectedReceivables.includes(r.id)}
                                                    onChange={checked => setSelectedReceivables(prev => checked ? [...prev, r.id] : prev.filter(id => id !== r.id))} />
                                            )}
                                        </td>
                                    )}
                                    <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white"
                                        title={`Cedente: ${operationCode}\nNFe: ${r.nfeKey}\nParcela: ${r.receivableNumber}`}>
                                        {r.titleNumber}
                                    </td>
                                    <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white text-center">{r.receivableNumber}</td>
                                    <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">{formatDate(r.dueDate)}</td>
                                    <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white text-right">{formatCurrency(r.amount)}</td>
                                    <td className="py-1.5 px-2 border-b border-gray-200 dark:border-slate-700 text-center">
                                        <CheckStatusBadge status={r.status} statusFormatted={r.statusFormatted || r.status} />
                                    </td>
                                    <td className="py-1.5 px-2 border-b border-gray-200 dark:border-slate-700 text-center">
                                        {r.canRevokeRejection && (
                                            <button onClick={() => handleUnmarkNotConfirmed(r.id)} className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-600" title="Desfazer não confirmado"><HiArrowsRightLeft size={15} /></button>
                                        )}
                                        {r.canReject && (
                                            <button onClick={() => { setNotConfirmId(r.id); setNotConfirmObs(''); setNotConfirmReason(''); }} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600" title="Marcar como não confirmado"><LuBookX size={15} /></button>
                                        )}
                                    </td>
                                </tr>
                                {r.observation && (
                                    <tr key={`${r.id}-obs-display`}>
                                        <td colSpan={showSelectColumn ? 7 : 6} className="px-2 py-1 bg-red-50/50 dark:bg-red-900/10 border-b border-gray-200 dark:border-slate-700 text-[10px] text-red-500 italic">
                                            {r.observation}
                                        </td>
                                    </tr>
                                )}
                                {notConfirmId === r.id && (
                                    <tr key={`${r.id}-obs`}>
                                        <td colSpan={showSelectColumn ? 7 : 6} className="px-2 py-2 bg-red-50 dark:bg-red-900/20 border-b border-gray-200 dark:border-slate-700">
                                            <div className="flex flex-col gap-2">
                                                <SelectTextField
                                                    label="Motivo"
                                                    value={notConfirmReason}
                                                    onChange={e => handleReasonChange(e.target.value)}
                                                    items={NOT_CONFIRM_REASONS.map(r => ({ value: r, label: r }))}
                                                    emptyLabel="Selecione o motivo..."
                                                />
                                                {notConfirmReason === 'Outro motivo' && (
                                                    <input type="text" value={notConfirmObs} onChange={e => setNotConfirmObs(e.target.value)} placeholder="Detalhe o motivo..."
                                                        autoFocus className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white" />
                                                )}
                                                <div className="flex gap-2 justify-end">
                                                    <button onClick={() => { setNotConfirmId(null); setNotConfirmReason(''); }} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">Cancelar</button>
                                                    <button onClick={() => handleMarkNotConfirmed(r.id)} disabled={!notConfirmObs}
                                                        className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50">Salvar</button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </ATable>
                </div>

                {/* History - fixed at bottom */}
                {history.length > 0 && (
                    <div className="flex-shrink-0 h-[200px] border-t border-gray-200 dark:border-slate-700 px-4 py-2">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Histórico</h4>
                        <div className="h-[160px] overflow-auto border border-gray-200 dark:border-slate-700 rounded">
                            <table className="w-full text-sm">
                                <tbody>
                                    {history.map((h: any, i: number) => (
                                        <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}>
                                            <td className="py-1.5 px-2 text-gray-500 whitespace-nowrap w-[140px]">{h.timestamp ? new Date(h.timestamp).toLocaleString('pt-BR') : '—'}</td>
                                            <td className="py-1.5 px-2 dark:text-white">{HISTORY_LABELS[h.action] || h.action}{h.channel ? ` (${h.channel})` : ''}{h.items?.length ? `: ${h.items.map((i: any) => `Nº ${i.titleNumber}/${i.receivableNumber}`).join(', ')}` : ''}</td>
                                            <td className="py-1.5 px-2 text-gray-400">{h.observation || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {showCallForm && contact && (
                <CallRegistryModalForm
                    contactId={contact.id}
                    interactionType="CALL"
                    lockReason="Validação"
                    availableDeals={deals as any}
                    preSelectedDeals={deals as any}
                    onClose={() => setShowCallForm(false)}
                    onSuccess={onCallSuccess}
                />
            )}

            {showInteractionForm && contact && (
                <CallRegistryModalForm
                    contactId={contact.id}
                    interactionType={showInteractionForm}
                    lockReason="Validação"
                    availableDeals={deals as any}
                    preSelectedDeals={deals as any}
                    onClose={() => setShowInteractionForm(null)}
                    onSuccess={(newActivity) => handleInteractionSuccess(showInteractionForm!, newActivity)}
                />
            )}

            {showContactForm && (
                <div className="absolute inset-0 bg-white dark:bg-slate-800 z-50 p-4 overflow-auto">
                    <ContactModalForm
                        onClose={() => setShowContactForm(false)}
                        onSuccess={handleContactCreated}
                        initialData={{ name: debtorName, document: debtorDocument }}
                    />
                </div>
            )}

            <ConfirmationModal
                isOpen={showUndoConfirm}
                onCancel={() => setShowUndoConfirm(false)}
                onConfirm={handleUndoValidation}
                title="Desfazer validação"
                id={draweeConfirmationId}
                text="Todos os status de recebíveis voltarão para 'Não iniciado' e as observações serão apagadas. Esta ação não pode ser revertida."
            />
        </div>
    );
};

export default DraweeConfirmationPageEdit;
